import { Recipe } from '@/types/api';
import { RecipeCard } from './RecipeCard';
import { cn } from '@/lib/utils';

interface RecipeGridProps {
  recipes: Recipe[];
  onFavoriteToggle?: (recipe: Recipe) => void;
  onAddToMealPlan?: (recipe: Recipe) => void;
  onViewRecipe?: (recipe: Recipe) => void;
  favoriteRecipeIds?: Set<string>;
  loading?: boolean;
  className?: string;
  variant?: 'default' | 'compact';
}

export function RecipeGrid({
  recipes,
  onFavoriteToggle,
  onAddToMealPlan,
  onViewRecipe,
  favoriteRecipeIds = new Set(),
  loading = false,
  className,
  variant = 'default'
}: RecipeGridProps) {
  if (loading) {
    return <RecipeGridSkeleton variant={variant} className={className} />;
  }

  if (recipes.length === 0) {
    return (
      <div className={cn("flex flex-col items-center justify-center py-12 text-center", className)}>
        <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
          <svg 
            className="w-8 h-8 text-gray-400" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.076 0-3.92.707-5.414 1.882M12 15v6.75M12 15V9a3 3 0 00-3-3H6a3 3 0 00-3 3v1.5A3 3 0 006 10.5h3m6 0a3 3 0 013 3v1.5a3 3 0 01-3 3h-3" 
            />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No se encontraron recetas</h3>
        <p className="text-gray-500 text-sm max-w-sm">
          Prueba ajustar tus filtros de búsqueda o explora diferentes términos.
        </p>
      </div>
    );
  }

  const gridCols = variant === 'compact' 
    ? "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
    : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4";

  return (
    <div className={cn(
      "grid gap-4",
      gridCols,
      className
    )}>
      {recipes.map((recipe) => (
        <RecipeCard
          key={recipe.id}
          recipe={recipe}
          onFavoriteToggle={onFavoriteToggle}
          onAddToMealPlan={onAddToMealPlan}
          onViewRecipe={onViewRecipe}
          isFavorite={favoriteRecipeIds.size > 0 ? favoriteRecipeIds.has(recipe.id) : undefined}
          variant={variant}
        />
      ))}
    </div>
  );
}

// Skeleton loader component
export function RecipeGridSkeleton({ 
  className, 
  variant = 'default', 
  count = 12 
}: { 
  className?: string; 
  variant?: 'default' | 'compact';
  count?: number;
}) {
  const gridCols = variant === 'compact' 
    ? "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
    : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4";

  const aspectRatio = variant === 'compact' ? "aspect-[4/3]" : "aspect-[3/2]";

  return (
    <div className={cn(
      "grid gap-4",
      gridCols,
      className
    )}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="bg-white rounded-lg border shadow-sm overflow-hidden">
          {/* Image Skeleton */}
          <div className={cn("bg-gray-200 animate-pulse", aspectRatio)} />
          
          {/* Content Skeleton */}
          <div className={cn("p-4", variant === 'compact' && "p-3")}>
            {/* Title */}
            <div className="h-4 bg-gray-200 rounded animate-pulse mb-2" />
            <div className="h-3 bg-gray-200 rounded animate-pulse mb-3 w-2/3" />
            
            {/* Meta info */}
            <div className="flex gap-3 mb-3">
              <div className="h-3 bg-gray-200 rounded animate-pulse w-16" />
              <div className="h-3 bg-gray-200 rounded animate-pulse w-20" />
            </div>
            
            {/* Tags (only for default variant) */}
            {variant === 'default' && (
              <div className="flex gap-1">
                <div className="h-5 bg-gray-200 rounded-full animate-pulse w-16" />
                <div className="h-5 bg-gray-200 rounded-full animate-pulse w-12" />
                <div className="h-5 bg-gray-200 rounded-full animate-pulse w-20" />
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
